import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@workspace/ui/components/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useForm,
} from '@workspace/ui/components/form';
import { Input } from '@workspace/ui/components/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@workspace/ui/components/select';
import { ArrowRight } from 'lucide-react';
import { useEffect, useState } from 'react';
import { m } from '~/paraglide/messages.js';
import {
  type IndividualProfileData,
  IndividualProfileSchema,
} from '../-schemas/onboarding-schema';

async function loadCountryLib() {
  const { Country } = await import('country-state-city');
  return Country;
}

type Props = {
  values: IndividualProfileData;
  onSubmit: (data: IndividualProfileData) => void;
};

const USE_CASES = [
  {
    value: 'payments',
    label: () => m['onboarding.individual_profile.usecase_payments'](),
  },
  {
    value: 'savings',
    label: () => m['onboarding.individual_profile.usecase_savings'](),
  },
  {
    value: 'cards',
    label: () => m['onboarding.individual_profile.usecase_cards'](),
  },
  {
    value: 'crypto',
    label: () => m['onboarding.individual_profile.usecase_crypto'](),
  },
  {
    value: 'other',
    label: () => m['onboarding.individual_profile.usecase_other'](),
  },
];

export function IndividualProfileForm({ values, onSubmit }: Props) {
  const [countries, setCountries] = useState<{ code: string; name: string }[]>(
    [],
  );
  const [countrySearch, setCountrySearch] = useState('');

  const form = useForm<IndividualProfileData>({
    resolver: zodResolver(IndividualProfileSchema),
    defaultValues: values,
  });

  useEffect(() => {
    loadCountryLib().then((lib) => {
      setCountries(
        lib.getAllCountries().map((c) => ({ code: c.isoCode, name: c.name })),
      );
    });
  }, []);

  const filteredCountries = countries.filter((c) =>
    c.name.toLowerCase().includes(countrySearch.toLowerCase()),
  );

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="w-full max-w-md space-y-5"
      >
        <div className="space-y-4 rounded-xl border border-border p-6">
          <FormField
            control={form.control}
            name="useCase"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="gap-0">
                  {m['onboarding.individual_profile.use_case']()}
                  <span className="text-destructive">*</span>
                </FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger className="w-full">
                      <SelectValue
                        placeholder={m[
                          'onboarding.individual_profile.use_case_placeholder'
                        ]()}
                      />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {USE_CASES.map((uc) => (
                      <SelectItem key={uc.value} value={uc.value}>
                        {uc.label()}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="country"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="gap-0">
                  {m['onboarding.individual_profile.country']()}
                  <span className="text-destructive">*</span>
                </FormLabel>
                <Select
                  onValueChange={(value) => {
                    field.onChange(value);
                    setCountrySearch('');
                  }}
                  defaultValue={field.value}
                  onOpenChange={(open) => {
                    if (!open) setCountrySearch('');
                  }}
                >
                  <FormControl>
                    <SelectTrigger className="w-full">
                      <SelectValue
                        placeholder={m[
                          'onboarding.individual_profile.country_placeholder'
                        ]()}
                      />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <div className="px-2 pb-2">
                      <Input
                        placeholder={m[
                          'onboarding.individual_profile.search_country'
                        ]()}
                        value={countrySearch}
                        onChange={(e) => setCountrySearch(e.target.value)}
                        className="h-9"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') e.preventDefault();
                        }}
                      />
                    </div>
                    <div className="max-h-[300px] overflow-y-auto">
                      {filteredCountries.length === 0 ? (
                        <div className="py-6 text-center text-sm text-muted-foreground">
                          {m[
                            'organization.business_address.no_countries_found'
                          ]()}
                        </div>
                      ) : (
                        filteredCountries.map((c) => (
                          <SelectItem key={c.code} value={c.code}>
                            {c.name}
                          </SelectItem>
                        ))
                      )}
                    </div>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <Button
          type="submit"
          className="flex w-full items-center justify-center gap-2 py-6 font-semibold transition-all"
        >
          {m['common.continue']()}
          <ArrowRight className="h-5 w-5" />
        </Button>
      </form>
    </Form>
  );
}
